import { CampaignStepId } from 'modules/campaigns/campaign-steps/types/campaign-step-interface'
import { EMAIL_STATISTICS_API } from 'modules/mailing/email-statistics/api/email-statistics-api'
import { StatisticsFilterApiInterface } from 'modules/mailing/email-statistics/types/email-statistics-filter-interface'
import { MailingStatisticsApiInterface } from 'modules/mailing/email-statistics/types/email-statistics-interface'
import { useCallback, useEffect, useRef } from 'react'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import { CountInterface, SwrPaginationInterface } from 'shared/types/pagination-interface'
import { getFilterQueryString } from 'shared/utils/api/get-filter-query-string'
import { getPaginationAndFilterQueryString } from 'shared/utils/api/get-pagination-and-filter-query-string'
import { baseFetcher } from 'shared/utils/http-client'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'

interface UseNewslettersInterface extends SwrPaginationInterface {
  emailId: CampaignStepId
  filter: StatisticsFilterApiInterface
}

export function useEmailStatistics({
  limit,
  pagination,
  emailId,
  filter,
}: UseNewslettersInterface) {
  const { user } = useUser()
  const { fetcher } = useFetch<RequestMethodsEnum.get, MailingStatisticsApiInterface>({
    method: RequestMethodsEnum.get,
  })

  const savedStatistics = useRef<MailingStatisticsApiInterface | undefined>(undefined)

  const {
    data: statistics,
    isValidating: isStatisticsValidating,
    mutate,
    abort: abortList,
  } = useControlledSwr(
    () =>
      user && [
        `${EMAIL_STATISTICS_API}/${emailId}/statistics/list?${getPaginationAndFilterQueryString({
          filter: { ...filter },
          pagination,
          limit,
        })}`,
        RequestModuleEnum.Contacts,
      ],
    fetcher,
  )

  const { data: counter, abort: abortCounter } = useControlledSwr<CountInterface>(
    () =>
      user &&
      `${EMAIL_STATISTICS_API}/${emailId}/statistics/count?${getFilterQueryString({
        filter: { ...filter },
      })}`,
    baseFetcher,
  )

  useEffect(() => {
    savedStatistics.current = statistics || savedStatistics.current
  }, [statistics])

  const abortStatistics = useCallback(() => {
    abortList()
    abortCounter()
  }, [abortCounter, abortList])

  return {
    statistics: statistics || savedStatistics.current,
    isStatisticsValidating,
    mutate,
    counter,
    abortStatistics,
  }
}
